/**
 * Enhanced ADA Compliance Script for Shopify Theme
 * Provides comprehensive accessibility improvements for the main site
 *
 * Features:
 * - ARIA live region management
 * - Focus management and keyboard navigation
 * - Screen reader announcements
 * - Form accessibility enhancements
 * - Image accessibility improvements
 * - Modal and popup accessibility
 * - Color contrast and visibility improvements
 */

class AccessibilityManager {
  constructor() {
    this.liveRegion = null;
    this.focusTrap = null;
    this.lastFocusedElement = null;
    this.isInitialized = false;

    this.init();
  }

  init() {
    if (this.isInitialized) return;

    this.createLiveRegion();
    this.enhanceKeyboardNavigation();
    // this.enhanceFormAccessibility();
    this.enhanceModalAccessibility();
    // this.addFocusIndicators();
    this.enhanceImageAccessibility();
    this.setupEventListeners();

    this.isInitialized = true;
    console.log('ADA Accessibility Manager initialized');
  }

  /**
   * Create ARIA live region for announcements
   */
  createLiveRegion() {
    if (document.getElementById('ada-live-region')) return;

    this.liveRegion = document.createElement('div');
    this.liveRegion.id = 'ada-live-region';
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    this.liveRegion.className = 'sr-only';
    this.liveRegion.style.cssText = `
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    `;

    document.body.appendChild(this.liveRegion);
  }

  /**
   * Announce messages to screen readers
   */
  announce(message, priority = 'polite') {
    if (!this.liveRegion || !message) return;

    this.liveRegion.setAttribute('aria-live', priority);
    this.liveRegion.textContent = message;

    // Clear after announcement
    setTimeout(() => {
      this.liveRegion.textContent = '';
    }, 1000);
  }

  /**
   * Enhanced keyboard navigation
   */
  enhanceKeyboardNavigation() {
    // Improve focus management for dropdowns and menus
    document.addEventListener('keydown', (e) => {
      // Escape key handling for dropdowns and modals
      if (e.key === 'Escape') {
        this.handleEscapeKey();
      }

      // Arrow key navigation for menus
      if (e.target.closest('[role="menu"], [role="menubar"]')) {
        this.handleMenuNavigation(e);
      }

      // Tab trap for modals
      if (e.key === 'Tab' && this.focusTrap) {
        this.handleTabTrap(e);
      }
    });

    // Add keyboard support for elements that look clickable but aren't buttons
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        const target = e.target;
        if (target.classList.contains('card') ||
            target.classList.contains('product-card') ||
            target.hasAttribute('data-clickable')) {
          const link = target.querySelector('a');
          if (link) {
            e.preventDefault();
            link.click();
          }
        }
      }
    });
  }

  /**
   * Handle escape key for closing dropdowns and modals
   */
  handleEscapeKey() {
    // Close open dropdowns
    const openDropdowns = document.querySelectorAll('details[open]');
    openDropdowns.forEach(dropdown => {
      dropdown.removeAttribute('open');
    });

    // Close modals
    const openModals = document.querySelectorAll('.modal[open], .drawer[open]');
    openModals.forEach(modal => {
      modal.removeAttribute('open');
      this.restoreFocus();
    });

    // Close any custom overlays
    const overlays = document.querySelectorAll('.overlay.is-visible, .popup.is-visible');
    overlays.forEach(overlay => {
      overlay.classList.remove('is-visible');
    });
  }

  /**
   * Handle menu navigation with arrow keys
   */
  handleMenuNavigation(e) {
    const menu = e.target.closest('[role="menu"], [role="menubar"]');
    const items = Array.from(menu.querySelectorAll('[role="menuitem"], a, button'));
    const currentIndex = items.indexOf(e.target);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        items[nextIndex].focus();
        break;

      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        items[prevIndex].focus();
        break;

      case 'Home':
        e.preventDefault();
        items[0].focus();
        break;

      case 'End':
        e.preventDefault();
        items[items.length - 1].focus();
        break;
    }
  }

  /**
   * Enhanced form accessibility
   */
  enhanceFormAccessibility() {
    // Add proper error handling
    document.addEventListener('invalid', (e) => {
      const field = e.target;
      this.handleFieldError(field);
    }, true);

    // Enhance required field indicators
    const requiredFields = document.querySelectorAll('input[required], select[required], textarea[required]');
    requiredFields.forEach(field => {
      this.enhanceRequiredField(field);
    });

    // Add live validation feedback
    document.addEventListener('input', (e) => {
      if (e.target.matches('input, select, textarea')) {
        this.validateField(e.target);
      }
    });
  }

  /**
   * Handle field validation errors
   */
  handleFieldError(field) {
    const errorId = `${field.id || field.name}-error`;
    let errorElement = document.getElementById(errorId);

    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.id = errorId;
      errorElement.className = 'field-error';
      errorElement.setAttribute('role', 'alert');
      errorElement.style.cssText = 'color: #d32f2f; font-size: 0.875rem; margin-top: 0.25rem;';
      field.parentNode.appendChild(errorElement);
    }

    const errorMessage = field.validationMessage || 'This field is required';
    errorElement.textContent = errorMessage;
    field.setAttribute('aria-describedby', errorId);
    field.setAttribute('aria-invalid', 'true');

    this.announce(`Error: ${errorMessage}`, 'assertive');
  }

  /**
   * Enhance required fields with proper indicators
   */
  enhanceRequiredField(field) {
    const label = document.querySelector(`label[for="${field.id}"]`) ||
                 field.closest('label') ||
                 field.parentNode.querySelector('label');

    if (label && !label.querySelector('.required-indicator')) {
      const indicator = document.createElement('span');
      indicator.className = 'required-indicator';
      indicator.textContent = ' *';
      indicator.setAttribute('aria-label', 'required');
      indicator.style.color = '#d32f2f';
      label.appendChild(indicator);
    }
  }

  /**
   * Validate field and provide feedback
   */
  validateField(field) {
    const errorId = `${field.id || field.name}-error`;
    const errorElement = document.getElementById(errorId);

    if (field.checkValidity()) {
      field.removeAttribute('aria-invalid');
      if (errorElement) {
        errorElement.remove();
      }
    }
  }

  /**
   * Enhanced modal accessibility
   */
  enhanceModalAccessibility() {
    // Monitor for modal openings
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          const element = mutation.target;
          if (element.matches('.modal, .drawer, [role="dialog"]')) {
            if (element.hasAttribute('open')) {
              this.handleModalOpen(element);
            } else {
              this.handleModalClose(element);
            }
          }
        }
      });
    });

    observer.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'class']
    });
  }

  /**
   * Handle modal opening
   */
  handleModalOpen(modal) {
    this.lastFocusedElement = document.activeElement;

    // Set proper ARIA attributes
    if (!modal.hasAttribute('role')) {
      modal.setAttribute('role', 'dialog');
    }
    if (!modal.hasAttribute('aria-modal')) {
      modal.setAttribute('aria-modal', 'true');
    }

    // Find and set aria-labelledby if there's a heading
    const heading = modal.querySelector('h1, h2, h3, h4, h5, h6, [slot="header"]');
    if (heading && !modal.hasAttribute('aria-labelledby')) {
      if (!heading.id) {
        heading.id = `modal-title-${Date.now()}`;
      }
      modal.setAttribute('aria-labelledby', heading.id);
    }

    // Create focus trap
    this.createFocusTrap(modal);

    // Focus first focusable element
    setTimeout(() => {
      const firstFocusable = this.getFirstFocusableElement(modal);
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }, 100);
  }

  /**
   * Handle modal closing
   */
  handleModalClose(modal) {
    this.removeFocusTrap();
    this.restoreFocus();
  }

  /**
   * Create focus trap for modal
   */
  createFocusTrap(container) {
    const focusableElements = this.getFocusableElements(container);

    this.focusTrap = {
      container,
      firstElement: focusableElements[0],
      lastElement: focusableElements[focusableElements.length - 1]
    };
  }

  /**
   * Remove focus trap
   */
  removeFocusTrap() {
    this.focusTrap = null;
  }

  /**
   * Handle tab trapping in modals
   */
  handleTabTrap(e) {
    if (!this.focusTrap) return;

    const { firstElement, lastElement } = this.focusTrap;

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }

  /**
   * Get focusable elements
   */
  getFocusableElements(container) {
    const selector = `
      a[href]:not([disabled]),
      button:not([disabled]),
      textarea:not([disabled]),
      input[type="text"]:not([disabled]),
      input[type="radio"]:not([disabled]),
      input[type="checkbox"]:not([disabled]),
      input[type="email"]:not([disabled]),
      input[type="password"]:not([disabled]),
      input[type="number"]:not([disabled]),
      input[type="tel"]:not([disabled]),
      input[type="url"]:not([disabled]),
      input[type="search"]:not([disabled]),
      select:not([disabled]),
      [tabindex]:not([tabindex="-1"]):not([disabled])
    `;

    return Array.from(container.querySelectorAll(selector))
      .filter(el => !el.closest('[hidden]') && el.offsetParent !== null);
  }

  /**
   * Get first focusable element
   */
  getFirstFocusableElement(container) {
    return this.getFocusableElements(container)[0];
  }

  /**
   * Restore focus to previously focused element
   */
  restoreFocus() {
    if (this.lastFocusedElement && document.contains(this.lastFocusedElement)) {
      this.lastFocusedElement.focus();
    }
    this.lastFocusedElement = null;
  }

  /**
   * Add enhanced focus indicators
   */
  addFocusIndicators() {
    // Add focus indicator styles
    if (!document.getElementById('ada-focus-styles')) {
      const style = document.createElement('style');
      style.id = 'ada-focus-styles';
      style.textContent = `
        /* Enhanced focus indicators */
        :focus {
          outline: 2px solid #4A90E2 !important;
          outline-offset: 2px !important;
        }

        /* High contrast focus for dark backgrounds */
        .dark :focus,
        [data-color-scheme="dark"] :focus {
          outline-color: #87CEEB !important;
        }

        /* Custom focus for interactive cards */
        .product-card:focus,
        .card:focus,
        [data-clickable]:focus {
          outline: 2px solid #4A90E2 !important;
          outline-offset: 2px !important;
          box-shadow: 0 0 0 4px rgba(74, 144, 226, 0.1) !important;
        }

        /* Skip to content link */
        .skip-to-content:focus {
          position: fixed !important;
          top: 10px !important;
          left: 10px !important;
          z-index: 9999 !important;
          padding: 8px 16px !important;
          background: #000 !important;
          color: #fff !important;
          text-decoration: none !important;
          border-radius: 4px !important;
          font-size: 14px !important;
        }
      `;
      document.head.appendChild(style);
    }
  }

  /**
   * Enhance image accessibility
   */
  enhanceImageAccessibility() {
    // Find images without alt text and add appropriate alt attributes
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      if (!img.hasAttribute('alt')) {
        // Check if image is decorative or informative
        if (img.closest('.icon, .logo, .background')) {
          img.setAttribute('alt', '');
          img.setAttribute('role', 'presentation');
        } else {
          // Try to get context from nearby text
          const context = this.getImageContext(img);
          img.setAttribute('alt', context || 'Image');
        }
      }
    });

    // Handle lazy-loaded images
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.tagName === 'IMG') {
            this.enhanceImage(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Get context for image alt text
   */
  getImageContext(img) {
    // Check for nearby headings or labels
    const parent = img.closest('.product-card, .collection-card, .card');
    if (parent) {
      const heading = parent.querySelector('h1, h2, h3, h4, h5, h6');
      if (heading) {
        return heading.textContent.trim();
      }
    }

    // Check for data attributes
    if (img.dataset.product) {
      return img.dataset.product;
    }

    return null;
  }

  /**
   * Enhance individual image
   */
  enhanceImage(img) {
    if (!img.hasAttribute('alt')) {
      const context = this.getImageContext(img);
      img.setAttribute('alt', context || '');
    }
  }

  /**
   * Setup additional event listeners
   */
  setupEventListeners() {
    // Announce page changes for SPA-like behavior
    let currentPath = window.location.pathname;

    const observer = new MutationObserver(() => {
      if (window.location.pathname !== currentPath) {
        currentPath = window.location.pathname;
        const pageTitle = document.title;
        this.announce(`Navigated to ${pageTitle}`, 'polite');
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Announce cart updates
    document.addEventListener('cart:updated', (e) => {
      const count = e.detail?.count || 0;
      this.announce(`Cart updated. ${count} items in cart.`, 'polite');
    });

    // Announce search results
    document.addEventListener('search:loaded', (e) => {
      const count = e.detail?.count || 0;
      this.announce(`Search results loaded. ${count} results found.`, 'polite');
    });

    // Handle form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.checkValidity()) {
        this.announce('Form submitted successfully', 'polite');
      }
    });
  }

  /**
   * Add ARIA labels to unlabeled buttons
   */
  enhanceButtons() {
    const buttons = document.querySelectorAll('button:not([aria-label]):not([aria-labelledby])');
    buttons.forEach(button => {
      if (!button.textContent.trim()) {
        // Try to get context from icons or nearby text
        const icon = button.querySelector('svg, .icon');
        if (icon) {
          const classes = icon.className;
          if (classes.includes('search')) {
            button.setAttribute('aria-label', 'Search');
          } else if (classes.includes('cart')) {
            button.setAttribute('aria-label', 'Shopping cart');
          } else if (classes.includes('menu') || classes.includes('hamburger')) {
            button.setAttribute('aria-label', 'Menu');
          } else if (classes.includes('close')) {
            button.setAttribute('aria-label', 'Close');
          }
        }
      }
    });
  }

  /**
   * Enhance color contrast warnings
   */
  checkColorContrast() {
    // This is a simplified version - in production you'd want a more robust contrast checker
    const elements = document.querySelectorAll('*');
    elements.forEach(el => {
      const style = window.getComputedStyle(el);
      const bg = style.backgroundColor;
      const color = style.color;

      // Add data attribute for potential styling adjustments
      if (bg !== 'rgba(0, 0, 0, 0)' && color !== 'rgba(0, 0, 0, 0)') {
        el.dataset.hasBackground = 'true';
      }
    });
  }
}

// Initialize the accessibility manager when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.AccessibilityManager = new AccessibilityManager();
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.AccessibilityManager = new AccessibilityManager();
  });
} else {
  window.AccessibilityManager = new AccessibilityManager();
}

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AccessibilityManager;
}
